import CircularProgress from '@mui/material/CircularProgress';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import debounce from 'lodash/debounce';
import set from 'lodash/set';
import React, {
  memo,
  useMemo,
  CSSProperties,
  useCallback,
  forwardRef,
  useRef
} from 'react';

import KColors from '../../constants/colors';
import { useCombineRefs } from '../../hooks';
import KImage from '../Image';
import { KButtonProps } from '../types';
import { KRadiusValue, KSpacingValue, TypoHelper } from '../Typography';

const KButtonIcon = forwardRef<HTMLButtonElement, KButtonProps>(
  (props, ref) => {
    const {
      icon,
      assetIcon,
      margin,
      marginB,
      marginH,
      marginL,
      marginR,
      marginT,
      marginV,
      padding,
      negativePadding,
      br = 'round',
      kind,
      size,
      disabled,
      isLoading,
      stretch,
      background,
      tintColor,
      enhanceStyle = {},
      brW = 0,
      brC,
      hasShadow = false,
      tight = false,

      alignSelf,
      avoidParentPress,
      onPress,
      ...otherProps
    } = props;

    const innerRef = useRef<HTMLButtonElement>();
    const combinedRef = useCombineRefs<HTMLButtonElement>(ref, innerRef);

    const { height, icon: iconSize } = useMemo(() => {
      switch (size) {
        case 'xlg':
          return {
            height: 48,
            icon: 36
          };
        case 'lg':
          return {
            height: 44,
            icon: 32
          };
        case 'md':
          return {
            height: 40,
            icon: 28
          };
        case 'sm':
          return {
            height: 32,
            icon: 20
          };
        case 'xs':
          return {
            height: 28,
            icon: 16
          };
        default:
          return {
            height: 36,
            icon: 24
          };
      }
    }, [size]);

    const iconColor = useMemo(() => {
      return disabled
        ? KColors.opacity.grayDarker[10]
        : kind
        ? KColors[kind]?.normal
        : tintColor;
    }, [disabled, kind, tintColor]);

    const commonStyle: CSSProperties = useMemo(() => {
      const result = { backgroundColor: background, color: iconColor };
      if (!TypoHelper.isNullOrUndefined(margin)) {
        set(result, 'margin', TypoHelper.mapKSpacingToNumber(marginH));
      }
      if (!TypoHelper.isNullOrUndefined(marginH)) {
        set(result, 'marginLeft', TypoHelper.mapKSpacingToNumber(marginH));
        set(result, 'marginRight', TypoHelper.mapKSpacingToNumber(marginH));
      }
      if (!TypoHelper.isNullOrUndefined(marginV)) {
        set(result, 'marginBottom', TypoHelper.mapKSpacingToNumber(marginV));
        set(result, 'marginTop', TypoHelper.mapKSpacingToNumber(marginV));
      }
      if (!TypoHelper.isNullOrUndefined(marginB)) {
        set(result, 'marginBottom', TypoHelper.mapKSpacingToNumber(marginB));
      }
      if (!TypoHelper.isNullOrUndefined(marginT)) {
        set(result, 'marginTop', TypoHelper.mapKSpacingToNumber(marginT));
      }
      if (!TypoHelper.isNullOrUndefined(marginL)) {
        set(result, 'marginLeft', TypoHelper.mapKSpacingToNumber(marginL));
      }
      if (!TypoHelper.isNullOrUndefined(marginR)) {
        set(result, 'marginRight', TypoHelper.mapKSpacingToNumber(marginR));
      }
      set(result, 'opacity', disabled ? 0.5 : 1);
      set(
        result,
        'alignSelf',
        alignSelf ? alignSelf : stretch ? 'stretch' : 'center'
      );

      return result;
    }, [
      background,
      iconColor,
      margin,
      marginH,
      marginV,
      marginB,
      marginT,
      marginL,
      marginR,
      disabled,
      alignSelf,
      stretch
    ]);

    const styledButton: CSSProperties = useMemo(() => {
      const clone = { ...commonStyle };
      const _size = tight
        ? iconSize
        : padding
        ? KSpacingValue[padding] + height
        : negativePadding
        ? height - KSpacingValue[negativePadding]
        : height;

      set(clone, 'height', _size);
      set(clone, 'width', _size);
      set(clone, 'padding', 0);

      if (brW) {
        set(clone, 'borderColor', brC || tintColor);
        set(clone, 'borderWidth', brW);
        set(clone, 'borderStyle', 'solid');
      }

      set(clone, 'borderRadius', KRadiusValue[br]);

      return clone;
    }, [
      commonStyle,
      tight,
      iconSize,
      padding,
      height,
      negativePadding,
      brW,
      br,
      brC,
      tintColor
    ]);

    const onPressWrapper = useCallback(
      (e?: any) => {
        if (avoidParentPress) {
          e?.stopPropagation?.();
        }
        if (!isLoading) {
          onPress?.(e);
        }
      },
      [avoidParentPress, isLoading, onPress]
    );

    const renderIcon = useMemo(() => {
      if (!icon && !assetIcon) {
        return null;
      }

      if (isLoading) {
        return (
          <CircularProgress
            thickness={2}
            size={iconSize * 0.75}
            sx={{ color: tintColor }}
          />
        );
      }

      const style = { fontSize: iconSize };

      if (assetIcon) {
        return <Icon style={style}>{assetIcon}</Icon>;
      }

      if (icon) {
        return <KImage.MuiIcon icon={icon} style={style} />;
      }
    }, [assetIcon, icon, iconSize, isLoading, tintColor]);

    return (
      // @ts-ignore
      <IconButton
        ref={combinedRef}
        {...otherProps}
        disabled={disabled}
        onClick={debounce(onPressWrapper, 300, {
          leading: true,
          trailing: false
        })}
        style={{
          boxShadow: !hasShadow
            ? 'none'
            : '0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%)',
          ...styledButton,
          ...enhanceStyle
        }}
      >
        {renderIcon}
      </IconButton>
    );
  }
);

export default memo(KButtonIcon);
